// pages/PropertySearch.js
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Container,
  Grid2,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Redux Imports
import {
  selectSearchResults,
  selectIsLoading,
  selectSearchError,
  setSearchResults,
  setLoading,
  setError,
  setFilters,
  setSearchQuery,
  clearAllFilters,
  //   resetSearch,
  // IMPORT YOUR SEARCH THUNK HERE. Example:
  // searchProperties
} from "../features/search/searchSlice";
import { useLazySearchPropertiesQuery } from "../features/search/searchApiSlice";

import PropertyCardVertical from "../components/properties/PropertyCardVertical";
import PropertyMap from "../components/properties/PropertyMap";

// --- MAIN PAGE COMPONENT ---
const PropertySearch = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Redux Selectors
  const searchResults = useSelector(selectSearchResults);
  const isLoading = useSelector(selectIsLoading);
  const error = useSelector(selectSearchError);

  const [triggerSearch, { isFetching }] = useLazySearchPropertiesQuery();

  const handleClearFilters = () => {
    // 1. Clear Redux state
    dispatch(clearAllFilters());
    dispatch(setSearchQuery(""));
    dispatch(
      setSearchResults({ results: [], total: 0, page: 1, totalPages: 1 })
    );

    // 2. Navigate to clean URL
    navigate("/search");
  };

  // Parse Properties from Redux
  const properties = useMemo(() => {
    if (!searchResults) return [];
    if (Array.isArray(searchResults)) return searchResults;
    if (searchResults.data) return searchResults.data;
    if (searchResults.results) return searchResults.results;
    return [];
  }, [searchResults]);

  // Parse URL parameters and fetch data
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // If no parameters, clear everything and show empty state
    if (params.toString() === "") {
      dispatch(clearAllFilters());
      dispatch(setSearchQuery(""));
      dispatch(
        setSearchResults({ results: [], total: 0, page: 1, totalPages: 1 })
      );
      return;
    }

    // Extract parameters from URL
    const query = params.get("q") || "";
    const suggestionId = params.get("suggestionId");
    const cityId = params.get("cityId");

    const minPrice = params.get("minPrice");
    const maxPrice = params.get("maxPrice");
    // const bedrooms = params.get("bedrooms");
    // const bathrooms = params.get("bathrooms");
    const gender = params.get("gender");
    const tenantType = params.get("tenantType");
    const sharingType = params.get("sharingType");
    const bathroomType = params.get("bathroomType");
    // const propertyType = params.get("propertyType");
    const amenities = params.get("amenities");

    // Update Redux filters from URL
    const filters = {};
    if (minPrice) filters.minPrice = parseInt(minPrice);
    if (maxPrice) filters.maxPrice = parseInt(maxPrice);
    // if (bedrooms) filters.bedrooms = parseInt(bedrooms);
    // if (bathrooms) filters.bathrooms = parseInt(bathrooms);
    if (gender) filters.gender = gender;
    if (tenantType) filters.tenantType = tenantType;
    if (sharingType) filters.sharingType = sharingType;
    if (bathroomType) filters.bathroomType = bathroomType;
    // if (propertyType) filters.propertyType = propertyType;
    if (amenities) filters.amenities = amenities.split(",");

    // Update filters in Redux
    dispatch(setSearchQuery(query));
    if (Object.keys(filters).length > 0) {
      dispatch(setFilters(filters));
    }

    // Build search parameters
    const searchParams = {
      query,
      suggestionId,
      cityId,
      ...filters,
    };

    // Only search if we have a query or filters
    if (query || suggestionId || cityId || Object.keys(filters).length > 0) {
      dispatch(setLoading(true));

      triggerSearch(searchParams)
        .then((response) => {
          if (response.data) {
            dispatch(
              setSearchResults({
                results: response.data?.data || response.data || [],
                total: response.data?.total || response.data?.count || 0,
                page: response.data?.page || 1,
                totalPages: response.data?.totalPages || 1,
              })
            );
          } else if (response.error) {
            dispatch(setError(response.error.data?.message || "Search failed"));
          }
        })
        .catch((err) => {
          dispatch(setError(err.message || "Search failed"));
        })
        .finally(() => {
          dispatch(setLoading(false));
        });
    }
  }, [location.search, dispatch, triggerSearch]);

  //   // Clear filters and reset search
  //   const handleClearFilters = () => {
  //     navigate("/search");
  //   };

  //   // Parse URL parameters and fetch data
  //   useEffect(() => {
  //     const params = new URLSearchParams(location.search);

  //     // Construct the search payload object from URL parameters
  //     const searchPayload = {
  //       query: params.get("q") || "",
  //       minPrice: params.get("minPrice"),
  //       maxPrice: params.get("maxPrice"),
  //       bedrooms: params.get("bedrooms"),
  //       amenities: params.get("amenities")
  //         ? params.get("amenities").split(",")
  //         : [],
  //       // ... map other params
  //     };

  //     // Dispatch the API call
  //     if (searchPayload.query || location.search) {
  //       console.log("Searching with:", searchPayload);
  //       // dispatch(searchProperties(searchPayload));
  //     }

  //     // const query = searchParams.get("q"); // Get 'q' from URL

  //     // Dispatch search action based on URL
  //     // Assumption: You have a thunk called 'searchProperties' that accepts a query string or object
  //     // if (query) {
  //     //   // dispatch(searchProperties({ query }));
  //     //   console.log("Fetching results for:", query);
  //     // } else {
  //     //   // If no query, maybe fetch all properties or reset?
  //     //   // dispatch(getAllProperties());
  //     // }

  //     // Cleanup on unmount
  //     // return () => {
  //     //   // Optional: keep results if you want back button to work fast
  //     //   // dispatch(resetSearch());
  //     // };
  //   }, [location.search, dispatch]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Scrollable Container for List */}
      <Box sx={{ flex: 1, overflowY: "auto", height: "100%" }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          {/* <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              {isLoading || isFetching
                ? "Searching..."
                : `${properties.length} PGs Found`} */}
          {/* : `${properties.length} Homes for Rent`} */}
          {/* </Typography> */}
          {/* Filter Buttons could go here */}
          {/* </Box> */}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid2 container spacing={3}>
              {/* LEFT COLUMN: Property List */}
              <Grid2 size={{ xs: 12, md: 7, lg: 7 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                  {isLoading || isFetching
                    ? "Searching..."
                    : `${properties.length} PGs Found`}
                  {/* : `${properties.length} Homes for Rent`} */}
                </Typography>
                {properties.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 5 }}>
                    <Typography variant="h6" color="text.secondary">
                      No PGs found. Try adjusting your search criteria.
                    </Typography>
                    <Button
                      variant="outlined"
                      sx={{ mt: 2 }}
                      //   onClick={() => navigate("/search")}
                      onClick={handleClearFilters}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                ) : (
                  <Grid2 container spacing={2}>
                    {properties.map((property) => (
                      <Grid2
                        size={{ xs: 12, sm: 6 }}
                        key={property._id || property.id}
                      >
                        <Link
                          to={`/property/${property._id || property.id}`}
                          style={{ textDecoration: "none" }}
                        >
                          <PropertyCardVertical property={property} />
                        </Link>
                      </Grid2>
                    ))}
                  </Grid2>
                )}
              </Grid2>

              {/* RIGHT COLUMN: Sticky Map */}
              {/* {!isMobile && ( */}
              <Grid2 size={{ xs: 12, md: 5, lg: 5 }}>
                <Box
                  sx={{
                    position: "sticky",
                    top: 20,
                    height: "calc(100vh - 130px)", // Dynamic height calculation
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: 3,
                  }}
                  //   sx={{
                  //     height: isMobile ? "400px" : "calc(100vh - 150px)", // Fixed height on desktop
                  //     borderRadius: 2,
                  //     overflow: "hidden",
                  //     boxShadow: 2,
                  //     position: isMobile ? "static" : "sticky",
                  //     top: 20, // Adjust for spacing
                  //   }}
                >
                  <PropertyMap properties={properties} />
                </Box>
              </Grid2>
              {/* )} */}
            </Grid2>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default PropertySearch;
